import React, { useState } from 'react';
import {
  adminFormInputClasses,
  adminFormTextareaClasses,
  adminFormTwoColGridClass,
} from '../shared/adminUx';
import {
  ANNOUNCEMENT_DEFAULTS,
  MAINTENANCE_DEFAULTS,
  SIGNUP_DEFAULTS,
} from './settingsDefaults';
import {
  SettingsConfirmModal,
  SettingsField,
  SettingsSectionShell,
  SettingsSuccessToast,
  useSettingsPanelState,
} from './SettingsSectionShell';

const SUBSECTIONS = [
  { id: 'maintenance', label: 'Maintenance Mode' },
  { id: 'customer-signup', label: 'Customer Sign-Up' },
  { id: 'business-signup', label: 'Business Sign-Up' },
  { id: 'announcement', label: 'Announcement Bar' },
];

const RadioGroup = ({ field, values, draft, setField }) => (
  <div className="flex flex-wrap gap-4 text-sm text-offblack">
    {values.map((value) => (
      <label key={value} className="flex items-center gap-2">
        <input
          type="radio"
          checked={draft[field] === value}
          onChange={() => setField(field, value)}
          className="w-4 h-4 text-primaryblue border-lightgray"
        />
        {value}
      </label>
    ))}
  </div>
);

const Checkbox = ({ field, label, draft, setField, disabled = false }) => (
  <label className={`flex items-center gap-2 text-sm ${disabled ? 'text-mediumgray' : 'text-offblack'}`}>
    <input
      type="checkbox"
      checked={!!draft[field]}
      disabled={disabled}
      onChange={(e) => setField(field, e.target.checked)}
      className="w-4 h-4 text-primaryblue border-lightgray rounded"
    />
    {label}
  </label>
);

const PlatformControlsPanel = () => {
  const [activeId, setActiveId] = useState('maintenance');
  const [confirm, setConfirm] = useState(null);

  const maintenance = useSettingsPanelState({ ...MAINTENANCE_DEFAULTS });
  const signup = useSettingsPanelState({ ...SIGNUP_DEFAULTS });
  const announcement = useSettingsPanelState({ ...ANNOUNCEMENT_DEFAULTS });

  const panels = {
    maintenance,
    'customer-signup': signup,
    'business-signup': signup,
    announcement,
  };
  const activePanel = panels[activeId];

  const validate = (draft) => {
    const errors = {};
    if (activeId === 'maintenance') {
      const scheduledOrActive = draft.status === 'Scheduled' || draft.status === 'Active Now';
      if (draft.status === 'Scheduled') {
        if (!draft.startTime) errors.startTime = 'Start Time is required.';
        if (!draft.endTime) errors.endTime = 'End Time is required.';
        if (draft.startTime && draft.endTime && draft.endTime <= draft.startTime) {
          errors.endTime = 'End Time must be later than Start Time.';
        }
      }
      if (scheduledOrActive) {
        const anyArea =
          draft.publicWebsite ||
          draft.customerDashboard ||
          draft.businessDashboard ||
          draft.adminPanel;
        if (!anyArea) errors.affectedAreas = 'Select at least one affected area.';
        if (!draft.internalReason?.trim()) errors.internalReason = 'Internal Reason is required.';
        const publicFacing =
          draft.publicWebsite || draft.customerDashboard || draft.businessDashboard;
        if (publicFacing && !draft.maintenanceMessage?.trim()) {
          errors.maintenanceMessage = 'Maintenance Message is required.';
        }
      }
    }
    if (activeId === 'customer-signup' && draft.customerStatus === 'Paused') {
      if (!draft.customerPublicNotice?.trim()) {
        errors.customerPublicNotice = 'Public Notice is required while sign-up is paused.';
      }
      if (!draft.customerInternalReason?.trim()) {
        errors.customerInternalReason = 'Internal Reason is required.';
      }
    }
    if (activeId === 'business-signup' && draft.businessStatus === 'Paused') {
      if (!draft.businessPublicNotice?.trim()) {
        errors.businessPublicNotice = 'Public Notice is required while sign-up is paused.';
      }
      if (!draft.businessInternalReason?.trim()) {
        errors.businessInternalReason = 'Internal Reason is required.';
      }
    }
    if (activeId === 'announcement') {
      const on = draft.status === 'Scheduled' || draft.status === 'Live';
      if (on) {
        if (!draft.message?.trim()) errors.message = 'Message is required.';
        const anyScope = draft.publicWebsite || draft.customerDashboard || draft.businessDashboard;
        if (!anyScope) errors.displayScope = 'Select at least one display scope.';
      }
      if (draft.linkLabel?.trim() && !draft.linkUrl?.trim()) {
        errors.linkUrl = 'Link URL is required when Link Label is entered.';
      }
      if (draft.status === 'Scheduled') {
        if (!draft.startTime) errors.startTime = 'Start Time is required.';
        if (!draft.endTime) errors.endTime = 'End Time is required.';
        if (draft.startTime && draft.endTime && draft.endTime <= draft.startTime) {
          errors.endTime = 'End Time must be later than Start Time.';
        }
      }
    }
    return errors;
  };

  const successFor = () => {
    if (activeId === 'maintenance') {
      if (maintenance.draft.status === 'Scheduled') return 'Maintenance scheduled.';
      if (maintenance.draft.status === 'Active Now') return 'Maintenance activated.';
      return 'Maintenance ended.';
    }
    if (activeId === 'announcement') {
      if (announcement.draft.status === 'Live') return 'Announcement published.';
      if (announcement.draft.status === 'Scheduled') return 'Announcement scheduled.';
      return 'Announcement turned off.';
    }
    if (activeId === 'customer-signup') return 'Customer Sign-Up settings updated.';
    if (activeId === 'business-signup') return 'Business Sign-Up settings updated.';
    return 'Platform controls updated.';
  };

  const requestSave = () => {
    const errors = validate(activePanel.draft);
    activePanel.setErrors(errors);
    if (Object.keys(errors).length) return;

    if (activeId === 'maintenance') {
      if (activePanel.draft.status === 'Active Now') {
        setConfirm({
          title: 'Activate Maintenance Mode?',
          message: 'The selected Trades Poster areas will become unavailable.',
        });
        return;
      }
      if (activePanel.draft.status === 'Scheduled') {
        setConfirm({
          title: 'Schedule Maintenance Mode?',
          message: 'The selected Trades Poster areas will become unavailable between the scheduled times.',
        });
        return;
      }
      if (activePanel.saved.status !== 'Off') {
        setConfirm({
          title: 'End Maintenance Mode?',
          message: 'Maintenance will end and the saved schedule will not start later.',
        });
        return;
      }
    }
    if (activeId === 'customer-signup' && activePanel.draft.customerStatus === 'Paused') {
      setConfirm({
        title: 'Pause Customer Sign-Up?',
        message: 'Visitors will be unable to create new Customer accounts.',
      });
      return;
    }
    if (activeId === 'business-signup' && activePanel.draft.businessStatus === 'Paused') {
      setConfirm({
        title: 'Pause Business Sign-Up?',
        message: 'Visitors will be unable to create new Business accounts.',
      });
      return;
    }
    if (activeId === 'announcement') {
      if (activePanel.draft.status === 'Live') {
        setConfirm({
          title: 'Publish Announcement?',
          message: 'This message will appear in the selected Trades Poster areas.',
        });
        return;
      }
      if (activePanel.draft.status === 'Scheduled') {
        setConfirm({
          title: 'Schedule Announcement?',
          message: 'This message will appear in the selected Trades Poster areas between the scheduled times.',
        });
        return;
      }
      if (activePanel.saved.status !== 'Off') {
        setConfirm({
          title: 'Turn Off Announcement?',
          message: 'The announcement will stop displaying and its saved schedule will not publish later.',
        });
        return;
      }
    }
    performSave();
  };

  const performSave = async () => {
    setConfirm(null);
    await activePanel.save(validate, successFor());
  };

  const dMaint = maintenance.draft;
  const scheduledOrActive = dMaint.status === 'Scheduled' || dMaint.status === 'Active Now';
  const dAnn = announcement.draft;
  const announcementOn = dAnn.status === 'Scheduled' || dAnn.status === 'Live';

  const renderSignup = (type) => {
    const isCustomer = type === 'customer';
    const statusField = isCustomer ? 'customerStatus' : 'businessStatus';
    const noticeField = isCustomer ? 'customerPublicNotice' : 'businessPublicNotice';
    const reasonField = isCustomer ? 'customerInternalReason' : 'businessInternalReason';
    const d = signup.draft;
    const paused = d[statusField] === 'Paused';
    return (
      <>
        <SettingsField label={isCustomer ? 'Customer Sign-Up Status' : 'Business Sign-Up Status'} required>
          <RadioGroup field={statusField} values={['Open', 'Paused']} draft={d} setField={signup.setField} />
        </SettingsField>
        {paused ? (
          <>
            <SettingsField label="Public Notice" required error={signup.errors[noticeField]}>
              <textarea
                rows={3}
                value={d[noticeField]}
                onChange={(e) => signup.setField(noticeField, e.target.value)}
                className={adminFormTextareaClasses()}
              />
            </SettingsField>
            <SettingsField label="Internal Reason" required error={signup.errors[reasonField]}>
              <textarea
                rows={3}
                value={d[reasonField]}
                onChange={(e) => signup.setField(reasonField, e.target.value)}
                className={adminFormTextareaClasses()}
              />
            </SettingsField>
          </>
        ) : null}
      </>
    );
  };

  const body = {
    maintenance: (
      <>
        <SettingsField label="Status" required>
          <RadioGroup
            field="status"
            values={['Off', 'Scheduled', 'Active Now']}
            draft={dMaint}
            setField={maintenance.setField}
          />
        </SettingsField>
        {dMaint.status === 'Scheduled' ? (
          <div className={adminFormTwoColGridClass}>
            <SettingsField label="Start Time" required error={maintenance.errors.startTime}>
              <input
                type="datetime-local"
                value={dMaint.startTime}
                onChange={(e) => maintenance.setField('startTime', e.target.value)}
                className={adminFormInputClasses(false)}
              />
            </SettingsField>
            <SettingsField label="End Time" required error={maintenance.errors.endTime}>
              <input
                type="datetime-local"
                value={dMaint.endTime}
                onChange={(e) => maintenance.setField('endTime', e.target.value)}
                className={adminFormInputClasses(false)}
              />
            </SettingsField>
          </div>
        ) : null}
        {scheduledOrActive ? (
          <>
            <SettingsField label="Affected Areas" required error={maintenance.errors.affectedAreas}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Checkbox field="publicWebsite" label="Public Website" draft={dMaint} setField={maintenance.setField} />
                <Checkbox field="customerDashboard" label="Customer Dashboard" draft={dMaint} setField={maintenance.setField} />
                <Checkbox field="businessDashboard" label="Business Dashboard" draft={dMaint} setField={maintenance.setField} />
                <Checkbox field="adminPanel" label="Admin Panel" draft={dMaint} setField={maintenance.setField} />
              </div>
            </SettingsField>
            {dMaint.publicWebsite || dMaint.customerDashboard || dMaint.businessDashboard ? (
              <SettingsField label="Maintenance Message" required error={maintenance.errors.maintenanceMessage}>
                <textarea
                  rows={3}
                  value={dMaint.maintenanceMessage}
                  onChange={(e) => maintenance.setField('maintenanceMessage', e.target.value)}
                  className={adminFormTextareaClasses()}
                />
              </SettingsField>
            ) : null}
            <SettingsField label="Internal Reason" required error={maintenance.errors.internalReason}>
              <textarea
                rows={3}
                value={dMaint.internalReason}
                onChange={(e) => maintenance.setField('internalReason', e.target.value)}
                className={adminFormTextareaClasses()}
              />
            </SettingsField>
          </>
        ) : null}
        <SettingsField label="Platform Owner Maintenance Access">
          <Checkbox
            field="platformOwnerMaintenanceAccess"
            label="On"
            draft={{ platformOwnerMaintenanceAccess: true }}
            setField={() => {}}
            disabled
          />
        </SettingsField>
      </>
    ),
    'customer-signup': renderSignup('customer'),
    'business-signup': renderSignup('business'),
    announcement: (
      <>
        <SettingsField label="Status" required>
          <RadioGroup
            field="status"
            values={['Off', 'Scheduled', 'Live']}
            draft={dAnn}
            setField={announcement.setField}
          />
        </SettingsField>
        {announcementOn ? (
          <>
            <SettingsField label="Message" required error={announcement.errors.message}>
              <textarea
                rows={3}
                value={dAnn.message}
                onChange={(e) => announcement.setField('message', e.target.value)}
                className={adminFormTextareaClasses()}
              />
            </SettingsField>
            <SettingsField label="Display Scope" required error={announcement.errors.displayScope}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <Checkbox field="publicWebsite" label="Public Website" draft={dAnn} setField={announcement.setField} />
                <Checkbox field="customerDashboard" label="Customer Dashboard" draft={dAnn} setField={announcement.setField} />
                <Checkbox field="businessDashboard" label="Business Dashboard" draft={dAnn} setField={announcement.setField} />
              </div>
            </SettingsField>
          </>
        ) : null}
        <div className={adminFormTwoColGridClass}>
          <SettingsField label="Link Label">
            <input
              type="text"
              value={dAnn.linkLabel}
              onChange={(e) => announcement.setField('linkLabel', e.target.value)}
              className={`w-full ${adminFormInputClasses(false)}`}
            />
          </SettingsField>
          {dAnn.linkLabel ? (
            <SettingsField label="Link URL" required error={announcement.errors.linkUrl}>
              <input
                type="url"
                value={dAnn.linkUrl}
                onChange={(e) => announcement.setField('linkUrl', e.target.value)}
                className={`w-full ${adminFormInputClasses(false)}`}
              />
            </SettingsField>
          ) : null}
        </div>
        {dAnn.status === 'Scheduled' ? (
          <div className={adminFormTwoColGridClass}>
            <SettingsField label="Start Time" required error={announcement.errors.startTime}>
              <input
                type="datetime-local"
                value={dAnn.startTime}
                onChange={(e) => announcement.setField('startTime', e.target.value)}
                className={adminFormInputClasses(false)}
              />
            </SettingsField>
            <SettingsField label="End Time" required error={announcement.errors.endTime}>
              <input
                type="datetime-local"
                value={dAnn.endTime}
                onChange={(e) => announcement.setField('endTime', e.target.value)}
                className={adminFormInputClasses(false)}
              />
            </SettingsField>
          </div>
        ) : null}
        <SettingsField label="Allow Dismissal" required>
          <Checkbox
            field="allowDismissal"
            label="Allow dismissal"
            draft={dAnn}
            setField={announcement.setField}
          />
        </SettingsField>
      </>
    ),
  }[activeId];

  return (
    <>
      <SettingsSectionShell
        subsections={SUBSECTIONS}
        activeId={activeId}
        onSelectSubsection={setActiveId}
        hasChanges={activePanel.hasChanges}
        saving={activePanel.saving}
        onSave={requestSave}
        onCancel={activePanel.cancel}
      >
        {body}
      </SettingsSectionShell>

      {confirm ? (
        <SettingsConfirmModal
          title={confirm.title}
          message={confirm.message}
          onCancel={() => setConfirm(null)}
          onConfirm={performSave}
          confirming={activePanel.saving}
        />
      ) : null}

      <SettingsSuccessToast message={activePanel.successMessage} />
    </>
  );
};

export default PlatformControlsPanel;
